class CountdownTimerSection extends HTMLElement {
  connectedCallback() {
    this.countdownTimeZone = 'America/Los_Angeles';
    this.timeZoneFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: this.countdownTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    this.interval = null;
    this.signupToggle = this.querySelector('[data-countdown-signup-toggle]');
    this.signupPanel = this.querySelector('[data-countdown-signup-panel]');
    this.completionContent = this.querySelector('[data-countdown-complete]');
    this.unitNodes = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.valueNodes = {
      days: this.querySelector('[data-value="days"]'),
      hours: this.querySelector('[data-value="hours"]'),
      minutes: this.querySelector('[data-value="minutes"]'),
      seconds: this.querySelector('[data-value="seconds"]'),
    };
    this.separatorNodes = Array.from(this.querySelectorAll('[data-separator]'));
    this.daysEnabled = this.dataset.showDays === 'true';
    this.labelsEnabled = this.dataset.showLabels === 'true';
    this.expiryAction = this.dataset.expiryAction || 'hide';
    this.signupMode = this.dataset.signupMode || 'dropdown';
    this.signupFormId = this.normalizeSignupFormId(this.dataset.signupFormId || '');
    this.timerType = this.dataset.timerType || 'countdown_to_date';
    this.startMode = this.dataset.startMode || 'right_now';
    this.startAt = this.parseDate(this.dataset.startAt);
    this.endAt = this.parseDate(this.dataset.endAt);
    this.fixedMinutes = Number(this.dataset.fixedMinutes || 0);
    this.sessionKey = this.dataset.sessionKey || `countdown-timer:${this.id}`;
    this.recurringDays = (this.dataset.recurringDays || '')
      .split(',')
      .filter((day) => day !== '')
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day));
    this.recurringStartMinutes = Number(this.dataset.recurringStartMinutes || 0);
    this.recurringEndMinutes = Number(this.dataset.recurringEndMinutes || 0);
    this.isSignupPanelOpen = false;
    this.handleSignupToggleClick = this.handleSignupToggleClick.bind(this);

    if (this.signupToggle) {
      this.signupToggle.addEventListener('click', this.handleSignupToggleClick);
    }

    this.render();
    this.interval = window.setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    if (this.interval) {
      window.clearInterval(this.interval);
    }

    if (this.signupToggle) {
      this.signupToggle.removeEventListener('click', this.handleSignupToggleClick);
    }
  }

  parseDate(value) {
    if (!value) return null;
    const localDateTime = String(value).trim().match(
      /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (localDateTime) {
      const parsed = this.getDateInCountdownTimeZone({
        year: Number(localDateTime[1]),
        month: Number(localDateTime[2]),
        day: Number(localDateTime[3]),
        hour: Number(localDateTime[4] || 0),
        minute: Number(localDateTime[5] || 0),
        second: Number(localDateTime[6] || 0),
      });

      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  getDateInCountdownTimeZone(parts) {
    let utcTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour || 0,
      parts.minute || 0,
      parts.second || 0
    );

    for (let index = 0; index < 2; index += 1) {
      utcTime =
        Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour || 0,
          parts.minute || 0,
          parts.second || 0
        ) - this.getCountdownTimeZoneOffset(utcTime);
    }

    return new Date(utcTime);
  }

  getCountdownTimeZoneOffset(timestamp) {
    const parts = this.getCountdownTimeZoneParts(new Date(timestamp));
    const zonedTime = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );

    return zonedTime - timestamp;
  }

  getCountdownTimeZoneParts(date) {
    const parts = {};

    this.timeZoneFormatter.formatToParts(date).forEach((part) => {
      if (part.type !== 'literal') {
        parts[part.type] = Number(part.value);
      }
    });

    return parts;
  }

  addDaysToDateParts(parts, days) {
    const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
    date.setUTCDate(date.getUTCDate() + days);

    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1,
      day: date.getUTCDate(),
    };
  }

  getDayOfWeek(parts) {
    return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
  }

  pad(value) {
    return String(Math.max(0, value)).padStart(2, '0');
  }

  getNow() {
    return new Date();
  }

  normalizeSignupFormId(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return '';

    const openFormMatch = trimmed.match(/openForm['"\s,()]+\s*['"]([^'"]+)['"]/i);
    if (openFormMatch) return openFormMatch[1].trim();

    const klaviyoClassMatch = trimmed.match(/klaviyo-form-([A-Za-z0-9_-]+)/i);
    if (klaviyoClassMatch) return klaviyoClassMatch[1].trim();

    const quotedValueMatch = trimmed.match(/['"]([A-Za-z0-9_-]{4,})['"]/);
    if (quotedValueMatch) return quotedValueMatch[1].trim();

    return trimmed;
  }

  handleSignupToggleClick() {
    if (this.signupMode === 'popup' && this.signupFormId) {
      if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
        try {
          window.klaviyo.openForm(this.signupFormId);
          return;
        } catch (error) {
          // Fall through to legacy/open queue variants below.
        }
      }

      if (window._klOnsite && typeof window._klOnsite.openForm === 'function') {
        try {
          window._klOnsite.openForm([this.signupFormId]);
          return;
        } catch (error) {
          try {
            window._klOnsite.openForm(this.signupFormId);
            return;
          } catch (legacyError) {
            // Fall through to queue-based variants below.
          }
        }
      }

      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['openForm', this.signupFormId]);
        return;
      }

      window._klOnsite = window._klOnsite || [];
      window._klOnsite.push(['openForm', this.signupFormId]);
      return;
    }

    if (!this.signupPanel) return;

    this.isSignupPanelOpen = !this.isSignupPanelOpen;
    this.signupToggle.setAttribute('aria-expanded', String(this.isSignupPanelOpen));
    this.signupPanel.hidden = !this.isSignupPanelOpen;
  }

  render() {
    const now = this.getNow();
    const state = this.getState(now);

    if (state.status === 'hidden') {
      this.classList.add('is-hidden');
      this.classList.remove('is-complete', 'is-loading');
      return;
    }

    this.classList.remove('is-hidden');

    if (state.status === 'complete') {
      if (this.expiryAction === 'show_message' && this.completionContent) {
        this.classList.add('is-complete');
        this.classList.remove('is-loading');
        return;
      }

      this.classList.add('is-hidden');
      this.classList.remove('is-complete', 'is-loading');
      return;
    }

    this.classList.remove('is-complete', 'is-loading');
    this.updateUnits(state.remainingMs);
  }

  getState(now) {
    if (this.startMode === 'schedule_later' && this.startAt && now < this.startAt) {
      return { status: 'hidden' };
    }

    if (this.timerType === 'fixed_minutes') {
      const endAt = this.getFixedMinutesEndAt(now);
      if (!endAt) return { status: 'hidden' };
      const remainingMs = endAt.getTime() - now.getTime();
      return remainingMs <= 0 ? { status: 'complete' } : { status: 'running', remainingMs };
    }

    if (this.timerType === 'daily_recurring') {
      const interval = this.getActiveRecurringInterval(now);
      if (!interval) return { status: 'hidden' };
      const remainingMs = interval.end.getTime() - now.getTime();
      return remainingMs <= 0 ? { status: 'complete' } : { status: 'running', remainingMs };
    }

    if (!this.endAt) {
      return { status: 'hidden' };
    }

    const remainingMs = this.endAt.getTime() - now.getTime();
    return remainingMs <= 0 ? { status: 'complete' } : { status: 'running', remainingMs };
  }

  getFixedMinutesEndAt(now) {
    if (this.fixedMinutes <= 0) return null;

    const storedEndAt = window.sessionStorage.getItem(this.sessionKey);
    if (storedEndAt) {
      const parsedEndAt = this.parseDate(storedEndAt);
      if (parsedEndAt) return parsedEndAt;
    }

    const endAt = new Date(now.getTime() + this.fixedMinutes * 60 * 1000);
    window.sessionStorage.setItem(this.sessionKey, endAt.toISOString());
    return endAt;
  }

  getActiveRecurringInterval(now) {
    const days = this.recurringDays.length ? this.recurringDays : [0, 1, 2, 3, 4, 5, 6];
    const today = this.getCountdownTimeZoneParts(now);

    for (let offset = -1; offset <= 0; offset += 1) {
      const startDate = this.addDaysToDateParts(today, offset);

      if (!days.includes(this.getDayOfWeek(startDate))) continue;
      if (this.startMode === 'schedule_later' && this.startAt) {
        const startOfDay = this.getDateInCountdownTimeZone(startDate);
        if (startOfDay < this.normalizeDate(this.startAt)) continue;
      }

      const intervalStart = this.getDateInCountdownTimeZone({
        year: startDate.year,
        month: startDate.month,
        day: startDate.day,
        hour: Math.floor(this.recurringStartMinutes / 60),
        minute: this.recurringStartMinutes % 60,
      });

      let endDate = startDate;

      if (this.recurringEndMinutes <= this.recurringStartMinutes) {
        endDate = this.addDaysToDateParts(startDate, 1);
      }

      const intervalEnd = this.getDateInCountdownTimeZone({
        year: endDate.year,
        month: endDate.month,
        day: endDate.day,
        hour: Math.floor(this.recurringEndMinutes / 60),
        minute: this.recurringEndMinutes % 60,
      });

      if (now >= intervalStart && now < intervalEnd) {
        return { start: intervalStart, end: intervalEnd };
      }
    }

    return null;
  }

  normalizeDate(date) {
    const parts = this.getCountdownTimeZoneParts(date);
    return this.getDateInCountdownTimeZone({
      year: parts.year,
      month: parts.month,
      day: parts.day,
    });
  }

  updateUnits(remainingMs) {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const daysVisible = this.daysEnabled && days > 0;

    if (this.unitNodes.days) {
      this.unitNodes.days.hidden = !daysVisible;
      this.valueNodes.days.textContent = this.pad(days);
    }

    this.valueNodes.hours.textContent = this.pad(hours);
    this.valueNodes.minutes.textContent = this.pad(minutes);
    this.valueNodes.seconds.textContent = this.pad(seconds);

    this.separatorNodes.forEach((separator) => {
      separator.hidden = separator.dataset.separator === 'days' ? !daysVisible : false;
    });

    if (!this.labelsEnabled) {
      this.querySelectorAll('[data-countdown-label]').forEach((node) => {
        node.hidden = true;
      });
    }
  }
}

if (!customElements.get('countdown-timer-section')) {
  customElements.define('countdown-timer-section', CountdownTimerSection);
}
